import ZoomVideoSDK, { type VideoPlayer } from "@zoom/videosdk";
import {
  getEnvFromHostname,
  RuntimeEnvironmentOptions,
} from "@growtherapy/analytics";
import { withTimeout } from "../../../../../utils";
import { LocalVideoDelegator } from "../../../../core/room/participant/media/MediaDelegator";
import {
  LocalVideoTrack,
  VideoClient,
  VirtualBackgroundArgs,
} from "../../../types";
import { handleError } from "../../../utils/handleError";
import { logger } from "../../../../../datadog/logger";
import { secondsToMilliseconds } from "date-fns";
import { videoSDKMutex } from "../../../../core/mutex";

type VideoRenderNode = {
  track: LocalVideoTrack;
  element: VideoPlayer;
};

type StartTrackArgs = {
  track: LocalVideoTrack;
  element: VideoPlayer;
} & VirtualBackgroundArgs;

type EnableArgs = {
  deviceId?: string;
} & VirtualBackgroundArgs;

/**
 * Returns the unique ID that Zoom maintains for the given element. Zoom
 * sets/changes this ID based on the associated track.
 */
function getElementNodeId(element: VideoPlayer | HTMLCanvasElement) {
  if ("node-id" in element) {
    return element["node-id"];
  }

  return element["id"];
}

/**
 * Unlike Twilio, Zoom's LocalVideoTrack, a device can only be enabled for one
 * `video-player` element at a time, even with different track
 * instances/elements.
 *
 * To work around this limitation, this delegator manages a stack of attached
 * elements and their associated tracks. When enabling, attaching, disabling, or
 * detaching, it ensures that only the top-most element in the stack has an
 * active video track.
 */
export class ZoomLocalPreviewVideoDelegator implements LocalVideoDelegator {
  readonly kind = "video" as const;
  #deviceId: string;
  #enabled: boolean;
  #nodes: Array<VideoRenderNode> = [];
  #virtualBackground: VirtualBackgroundArgs | undefined;
  #lastStartedAt: number | undefined;
  #videoClient: VideoClient;

  constructor(
    deviceId: string,
    enabled: boolean,
    videoClient: VideoClient,
    virtualBackground?: VirtualBackgroundArgs,
  ) {
    this.#deviceId = deviceId;
    this.#enabled = enabled;
    this.#virtualBackground = virtualBackground;
    this.#videoClient = videoClient;
  }

  get deviceId() {
    return this.#deviceId;
  }

  get isEnabled() {
    return this.#enabled;
  }

  async attach(element: VideoPlayer): Promise<void> {
    await videoSDKMutex.runExclusive(
      () => this.#attach(element),
      "ZoomLocalPreviewVideoDelegator#attach",
    );
  }

  async detach(element: VideoPlayer): Promise<void> {
    await videoSDKMutex.runExclusive(
      () => this.#detach(element),
      "ZoomLocalPreviewVideoDelegator#detach",
    );
  }

  // non-interface methods

  /**
   * ⚠️ To avoid Zoom video SDK weirdness, please ensure this method is awaited
   * and runs within the {@link videoSDKMutex}
   */
  async enable({ deviceId, ...virtualBackground }: EnableArgs = {}) {
    if (this.isInSession) {
      logger.warn("[ZoomLocalPreviewVideo]: Enable called while in session");
      return;
    }

    this.#logDebugInfo("[ZoomLocalPreviewVideo]: Enabling video track", {
      deviceId,
      virtualBackground,
    });

    this.#virtualBackground = virtualBackground.virtualBackgroundType
      ? virtualBackground
      : this.#virtualBackground;
    this.#enabled = true;

    const { element, track } = this.activeNode ?? {};

    if (!element || !track) {
      return;
    }

    const shouldSwitchCamera = deviceId && deviceId !== this.#deviceId;
    if (shouldSwitchCamera) {
      await this.#switchCamera(track, deviceId);
      this.#deviceId = deviceId;
    }

    await this.#startTrack({
      track,
      element,
      ...this.#virtualBackground,
    });
  }

  /**
   * ⚠️ To avoid Zoom video SDK weirdness, please ensure this method is awaited
   * and runs within the {@link videoSDKMutex}
   */
  async disable() {
    if (this.isInSession) {
      logger.warn("[ZoomLocalPreviewVideo]: Disable called while in session");
      return;
    }

    this.#logDebugInfo("[ZoomLocalPreviewVideo]: Disabling video track");
    const { track } = this.activeNode ?? {};
    if (!track) {
      return;
    }

    this.#enabled = false;

    await this.#stopTrack(track);
  }

  /**
   * Timestamp of when the last track start attempt was made, regardless of
   * whether it was successful or not.
   */
  get lastStartedAt() {
    return this.#lastStartedAt;
  }

  async #attach(element: VideoPlayer): Promise<void> {
    if (this.isInSession) {
      logger.warn("[ZoomLocalPreviewVideo]: Attach called while in session");
      return;
    }

    this.#logDebugInfo(
      "[ZoomLocalPreviewVideo]: Attaching video track to element",
      {
        elementNodeId: getElementNodeId(element),
        element,
      },
    );

    if (this.#findNode(element) || !this.#enabled) {
      return;
    }

    const track = ZoomVideoSDK.createLocalVideoTrack(this.#deviceId);

    const { track: activeTrack, element: activeElement } =
      this.activeNode ?? {};
    if (activeTrack && activeElement) {
      await this.#stopTrack(activeTrack).catch((error) => {
        handleError(
          "[ZoomLocalPreviewVideo]: attach -> stop active track",
          error,
        );
      });
    }

    try {
      await this.#startTrack({
        track,
        element,
        ...this.#virtualBackground,
      });
      this.#nodes.push({ track, element });
    } catch (attachVideoError) {
      handleError("[ZoomLocalPreviewVideo]: attach", attachVideoError);
    }
  }

  async #detach(element: VideoPlayer): Promise<void> {
    if (this.isInSession) {
      logger.warn("[ZoomLocalPreviewVideo]: Detach called while in session");
      return;
    }

    this.#logDebugInfo(
      "[ZoomLocalPreviewVideo]: Detaching video track from element",
      {
        elementNodeId: getElementNodeId(element),
        element,
      },
    );

    if (!element) {
      return;
    }

    const { track } = this.#findNode(element) ?? {};

    if (!track) {
      return;
    }

    try {
      await this.#stopTrack(track);
      this.#removeNodeById(getElementNodeId(element));

      // Restart the previous node
      const activeNode = this.activeNode;
      if (this.isEnabled && activeNode) {
        await this.#startTrack({
          track: activeNode.track,
          element: activeNode.element,
          ...this.#virtualBackground,
        }).catch((error) => {
          handleError(
            "[ZoomLocalPreviewVideo]: detach -> start new active track",
            error,
          );
        });
      }
    } catch (detachVideoError) {
      handleError("[ZoomLocalPreviewVideo]: detach", detachVideoError);
    }
  }

  async #switchCamera(track: LocalVideoTrack, deviceId: string) {
    // Assumes that Zoom handles freeing up the previous device
    await track.switchCamera(deviceId);
  }

  private get activeNode(): VideoRenderNode | undefined {
    return this.#nodes[this.#nodes.length - 1];
  }

  async #startTrack({ track, element, ...virtualBackground }: StartTrackArgs) {
    const { virtualBackgroundType, virtualBackgroundValue } = virtualBackground;
    if (virtualBackgroundType) {
      this.#virtualBackground = virtualBackground;
      await track.updateVirtualBackground(
        virtualBackgroundValue && virtualBackgroundValue.imageUrl,
      );
    }

    if (track.isVideoStarted) {
      return;
    }

    this.#lastStartedAt = Date.now();
    await withTimeout(
      () =>
        track.start(
          element,
          ...(virtualBackgroundType ? [virtualBackgroundValue] : []),
        ),
      secondsToMilliseconds(30),
      {
        timeoutMessage: "[ZoomLocalPreviewVideo]: startTrack timed out",
      },
    );
  }

  async #stopTrack(track: LocalVideoTrack) {
    if (track.isVideoStarted) {
      await track.stop();
    }
  }

  #findNode(element: VideoPlayer) {
    return this.#nodes.find((existingNode) => {
      return (
        getElementNodeId(existingNode.element) === getElementNodeId(element)
      );
    });
  }

  #removeNodeById(nodeId: string) {
    this.#nodes = this.#nodes.filter(
      (node) => getElementNodeId(node.element) !== nodeId,
    );
  }

  #logDebugInfo(...args: Parameters<typeof logger.debug>) {
    if (
      getEnvFromHostname({ hostname: location.hostname }) ===
      RuntimeEnvironmentOptions.local
    ) {
      const [message, context] = args;
      logger.debug(message, {
        this: {
          kind: this.kind,
          deviceId: this.#deviceId,
          enabled: this.#enabled,
          activeNode: { ...this.activeNode },
          nodes: [...this.#nodes],
          virtualBackground: { ...this.#virtualBackground },
        },
        ...context,
      });
    }
  }

  private get isInSession() {
    return this.#videoClient.getSessionInfo()?.isInMeeting;
  }
}
